/**
 * TypeScript mirror of the API's V_7.0 assistant contract.
 *
 * Source of truth:
 *   api/src/AdaptiveCatalog.Api.Shared/Models/V_7.0/MachineLearning/AssistantSession.cs
 *   api/src/AdaptiveCatalog.Api.Shared/Models/V_7.0/MachineLearning/AssistantActivity.cs
 *
 * Keep the two in step. DateTime values arrive as ISO strings over the wire, so they are typed as
 * string here rather than Date.
 */

/** An assistant a caller may open a session against. */
export interface IAssistantCapability {
    key?: string;
    name?: string;
    description?: string;
    /** False when the organization is not entitled. The list comes back either way. */
    enabled: boolean;
}

/**
 * A model this organization may run. Safe projection only: there is deliberately no base url, no
 * api key and no server-side model id in this shape.
 */
export interface IAssistantModel {
    key?: string;
    friendlyName?: string;
    vision: boolean;
    thinking: boolean;
    tools: boolean;
    contextWindow: number;
    maxOutputTokens: number;
    isDefault: boolean;
}

/** Current quota usage for the organization, so the dock can warn before a hard refusal. */
export interface IAssistantUsage {
    dailyRequests: number;
    dailyRequestLimit: number;
    /** Tokens spent this calendar month. Informational: nothing is refused because of it. */
    monthlyTokens: number;
    /** Storefront generations used inside the current rolling window. */
    generationUsed: number;
    /** How many generations the rolling window allows. */
    generationLimit: number;
    /** The rolling window length in days (by product level unless overridden). */
    generationWindowDays: number;
    /** When the oldest counted generation ages out and a slot frees up. Absent when none counted. */
    generationResetsAt?: string;
}

export interface IAssistantToolCall {
    id?: string;
    name?: string;
    /** Raw JSON arguments, for a details view. */
    arguments?: string;
}

export interface IAssistantMessage {
    role?: string;
    content?: string;
    /**
     * Captured chain of thought, present only when the platform is configured to log it. It is a
     * separate field on purpose and is never what the assistant said. Render it behind a
     * disclosure, never as the answer.
     */
    reasoning?: string;
    toolCalls?: IAssistantToolCall[];
    toolCallId?: string;
    toolName?: string;
    isError?: boolean;
    /**
     * True when this is written as a user turn but is really a tool's output. The OpenAI chat shape
     * only permits images on a user message, so a tool returning screenshots has to be written as
     * one. Hide these in the conversation: otherwise the tool's caption appears in the operator's own
     * speech bubble. The diagnostic transcript shows them in place.
     */
    isToolOutput?: boolean;
    timestamp?: string;
}

/** The structured output of propose_design, rendered as an approval card. */
export interface IAssistantDesignProposal {
    proposalId?: string;
    storefrontId?: string;
    name?: string;
    summary?: string;
    theme?: string;
    palette?: { [key: string]: string };
    fonts?: { [key: string]: string };
    layoutNotes?: string[];
    proposed?: string;
    approved: boolean;
    approvedAt?: string;
    approvedBy?: string;
}

export interface IAssistantSession {
    id?: string;
    capability?: string;
    user?: string;
    /** The storefront this session is working on. Null until the session picks or creates one. */
    targetId?: string;
    /** The model key pinned at session creation. A session never silently changes model. */
    modelKey?: string;
    status?: string;
    phase?: string;
    title?: string;
    /** Omitted on the list response. Fetch one session to get its transcript. */
    messages?: IAssistantMessage[];
    proposal?: IAssistantDesignProposal;
    proposalApproved: boolean;
    /** True once the assistant called request_publish. It is a request, never a publish. */
    publishRequested: boolean;
    published: boolean;
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
    /** Model time on this session, summed over turns. Excludes queue wait and operator think time. */
    processingMilliseconds: number;
    lastTurnMilliseconds: number;
    turnCount: number;
    /**
     * The storefront this session created for itself, set by the storefront-generation flow. Distinct
     * from targetId, which is also set when a session was pointed at a storefront that already
     * existed: cancelling a generation removes the storefront it created, and only this field says
     * which one that is.
     */
    createdStorefrontId?: string;
    /** The public website a storefront-generation session was asked to work from. */
    sourceWebsiteUrl?: string;
    /** What the operator typed into the generation form's comments box, verbatim. */
    generationComments?: string;
    /** Hosted full-page captures archived while the session ran: the source website and the storefront per turn. */
    screenshots?: { kind: 'website' | 'storefront'; url: string; taken?: string; turn?: number }[];
    /** 1-based place in the worker queue, populated only while Pending. Server-wide; a count, not a promise. */
    queuePosition?: number;
    /** Set when the session ended in Failed. Written for an operator to read. */
    error?: string;
    /** The session id currently holding this storefront's edit lease, when one does. */
    leaseHolder?: string;
    /**
     * True once somebody armed "notify me when it's done" on this session, so a reopened dialog shows
     * the armed state instead of offering the button again. Deliberately a boolean: who armed it, and
     * their email address, stay server-side.
     */
    notifyRequested?: boolean;
    created?: string;
    lastUpdated?: string;
}

export interface IAssistantSessionRequest {
    capability?: string;
    targetId?: string;
    modelKey?: string;
    title?: string;
    /** Optional opening message, enqueued with the session so the dock needs one call, not two. */
    message?: string;
}

export interface IAssistantMessageRequest {
    message: string;
}

/**
 * Start a storefront from a public website. The API creates the storefront itself and runs the
 * generation against it, so there is no targetId to supply and no storefront to clean up by hand if
 * the session is cancelled.
 */
export interface IStorefrontGenerationRequest {
    /** Absolute http/https address of the site to work from. */
    websiteUrl: string;
    subUrl: string;
    name?: string;
    /** Free-form styling or brand guidance, passed through as the opening instruction. */
    comments?: string;
}

/** Mirrors AssistantCancelReasons. Sent with a cancel so the survey answers are queryable. */
export const ASSISTANT_CANCEL_REASON = {
    BRAND_MISMATCH: 'brand-mismatch',
    STYLING_BROKEN: 'styling-broken',
    NOT_WHAT_I_ASKED: 'not-what-i-asked',
    TOO_SLOW: 'too-slow',
    JUST_EXPLORING: 'just-exploring',
    OTHER: 'other',
} as const;

export type AssistantCancelReasonCode = typeof ASSISTANT_CANCEL_REASON[keyof typeof ASSISTANT_CANCEL_REASON];

/**
 * The optional survey body on a cancel. Every field is optional on purpose: the cancel must never be
 * blocked on the user answering, so an empty body is a valid request.
 */
export interface IAssistantCancelRequest {
    reasonCode?: AssistantCancelReasonCode;
    comments?: string;
}

/** One auditable thing an assistant session did. Mirrors AssistantActivity.cs. */
export interface IAssistantActivity {
    id?: string;
    timestamp?: string;
    activityType?: string;
    capability?: string;
    sessionId?: string;
    user?: string;
    targetId?: string;
    toolName?: string;
    modelKey?: string;
    success: boolean;
    message?: string;
    changedFields?: string[];
    durationMs?: number;
}

/** Mirrors AssistantSessionStatus in the API. Held as strings so an old document still reads. */
export const ASSISTANT_STATUS = {
    PENDING: 'Pending',
    PROCESSING: 'Processing',
    AWAITING_APPROVAL: 'AwaitingApproval',
    COMPLETED: 'Completed',
    FAILED: 'Failed',
    CANCELLED: 'Cancelled',
} as const;

/** Mirrors AssistantSessionPhase. Advisory only, for the progress strip. */
export const ASSISTANT_PHASE = {
    DISCOVERY: 'Discovery',
    PROPOSAL: 'Proposal',
    APPLYING: 'Applying',
    REVIEW: 'Review',
    PUBLISHED: 'Published',
} as const;

/** Mirrors AssistantCapabilities. */
export const ASSISTANT_CAPABILITY_STOREFRONT_RESTYLE = 'storefrontRestyle';

/** Mirrors AssistantLimitNames. Arrives in ActionResponse.id on a 429. */
export const ASSISTANT_LIMIT = {
    DAILY_REQUESTS: 'dailyRequests',
    CONCURRENT_SESSIONS: 'concurrentSessions',
    SESSION_MESSAGES: 'sessionMessages',
} as const;

/** Mirrors AiRoles. */
export const ASSISTANT_ROLE = {
    SYSTEM: 'system',
    USER: 'user',
    ASSISTANT: 'assistant',
    TOOL: 'tool',
} as const;

/**
 * The session is waiting on the worker and the dock should keep polling. Failed is deliberately not
 * in here: it is recoverable but only a human sending another message restarts it.
 */
export const assistantSessionIsRunning = (status?: string): boolean =>
    status === ASSISTANT_STATUS.PENDING || status === ASSISTANT_STATUS.PROCESSING;

/** A new user message may be accepted. Mirrors AssistantSessionStatus.AcceptsMessages. */
export const assistantSessionAcceptsMessages = (status?: string): boolean =>
    status !== ASSISTANT_STATUS.PROCESSING && status !== ASSISTANT_STATUS.CANCELLED;

/**
 * Tools that write to the storefront. Seeing one of these land in a finished turn is what tells the
 * dock an open editor is now showing stale data.
 */
export const ASSISTANT_MUTATING_TOOLS: string[] = [
    'create_storefront',
    'update_branding',
    'update_style_config',
    'set_sections',
    'set_banners',
    'set_links',
    'set_localization',
];

/**
 * The raw transcript, for Adaptive Catalog staff debugging the assistant.
 *
 * Not what the dock renders. The dock shows a conversation and is deliberately lossy; while the
 * assistant is still being taught its job, the lossy parts are the ones worth reading.
 */
export interface IAssistantTranscript {
    sessionId?: string;
    status?: string;
    modelKey?: string;
    capability?: string;
    turnCount: number;
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
    processingMilliseconds: number;
    created?: string;
    lastUpdated?: string;
    entries?: IAssistantTranscriptEntry[];
}

/** One message exactly as it sits in the session document. */
export interface IAssistantTranscriptEntry {
    index: number;
    role?: string;
    content?: string;
    reasoning?: string;
    toolCalls?: IAssistantToolCall[];
    toolCallId?: string;
    toolName?: string;
    isError?: boolean;
    /** Survives context trimming. A transcript missing the operator's instruction usually really is. */
    pinned: boolean;
    /** Images are counted, never returned: the dock has already rendered them. */
    imageCount: number;
    timestamp?: string;
}

/**
 * Whether a signed-in user is Adaptive Catalog staff.
 *
 * Gates the diagnostic transcript button. Mirrors AdaptiveCatalogStaff.IsStaff on the API, which is
 * the check that actually enforces it: this one only decides whether to draw the button, and the
 * endpoint refuses regardless. Matches the separator too, so notadaptivecatalog.com and
 * evil.adaptivecatalog.com are not us.
 */
export const isAdaptiveCatalogStaff = (username?: string | null): boolean =>
    !!username && username.trim().toLowerCase().endsWith('@adaptivecatalog.com');
